import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './AuthContext'
import { isEMDOrAdmin, mapLegacyRole } from '../utils/roles'

export const RESERVED_ROOT_SLUGS = [
  'admin',
  'landing-pages',
  'landingpages',
  'r',
  'profile',
  'dashboard',
  'login',
  'register',
  'confirm-email',
  'services',
  'recruiting',
  'onboarding',
  'clients',
  'leads',
  'reports',
  'settings',
  'tools',
  'resources',
  'support'
]

const RedirectsContext = createContext({})

export const useRedirects = () => useContext(RedirectsContext)

const getAnalyticsFromRedirects = (redirects = []) => {
  const totalRedirects = redirects.length
  const totalClicks = redirects.reduce((sum, redirect) => sum + (redirect.click_count || 0), 0)
  const topRedirect = redirects.reduce((currentTop, redirect) => {
    if (!currentTop) return redirect
    const currentTopClicks = currentTop.click_count || 0
    const redirectClicks = redirect.click_count || 0
    return redirectClicks > currentTopClicks ? redirect : currentTop
  }, null)

  return {
    totalRedirects,
    totalClicks,
    topRedirect
  }
}

export const RedirectsProvider = ({ children }) => {
  const { user, professionalProfile } = useAuth()
  const [redirects, setRedirects] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [checkingSlug, setCheckingSlug] = useState(false)
  const [analytics, setAnalytics] = useState({
    totalRedirects: 0,
    totalClicks: 0,
    topRedirect: null
  })

  const normalizedRole = useMemo(
    () => mapLegacyRole(professionalProfile?.role) ?? null,
    [professionalProfile?.role]
  )

  const isAdmin = useMemo(() => {
    if (!normalizedRole) {
      return false
    }

    return normalizedRole === 'manager' || isEMDOrAdmin(normalizedRole)
  }, [normalizedRole])

  const refreshAnalytics = useCallback((items) => {
    setAnalytics(getAnalyticsFromRedirects(items))
  }, [])

  const fetchRedirects = useCallback(async () => {
    if (!user || !isAdmin) return

    setLoading(true)
    setError(null)

    const { data, error: fetchError } = await supabase
      .from('url_redirects')
      .select('*')
      .order('created_at', { ascending: false })

    if (fetchError) {
      console.error('Error fetching redirects:', fetchError)
      setError(fetchError.message)
      setRedirects([])
      setAnalytics({ totalRedirects: 0, totalClicks: 0, topRedirect: null })
    } else {
      const normalized = (data || []).map((redirect) => ({
        ...redirect,
        redirect_type: redirect.redirect_type || 'short'
      }))
      setRedirects(normalized)
      refreshAnalytics(normalized)
    }

    setLoading(false)
  }, [isAdmin, refreshAnalytics, user])

  useEffect(() => {
    if (isAdmin && user) {
      fetchRedirects()
    } else {
      setRedirects([])
      setAnalytics({ totalRedirects: 0, totalClicks: 0, topRedirect: null })
    }
  }, [fetchRedirects, isAdmin, user])

  const sanitizeSlug = useCallback((value = '') => {
    return value
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
  }, [])

  const checkSlugAvailability = useCallback(
    async (slug, redirectTypeOrExcludeId = 'short', maybeExcludeId = null) => {
      let redirectType = 'short'
      let excludeId = null

      if (redirectTypeOrExcludeId === 'root' || redirectTypeOrExcludeId === 'short') {
        redirectType = redirectTypeOrExcludeId
        excludeId = maybeExcludeId
      } else if (redirectTypeOrExcludeId) {
        excludeId = redirectTypeOrExcludeId
      } else if (maybeExcludeId) {
        excludeId = maybeExcludeId
      }

      const cleanedSlug = sanitizeSlug(slug)

      if (!cleanedSlug) {
        return { available: false, reason: 'Slug is required' }
      }

      if (redirectType === 'root' && RESERVED_ROOT_SLUGS.includes(cleanedSlug)) {
        return { available: false, reason: 'This slug conflicts with an existing page' }
      }

      setCheckingSlug(true)
      try {
        const { data: existingRedirect, error: slugError } = await supabase
          .from('url_redirects')
          .select('id')
          .eq('slug', cleanedSlug)
          .eq('redirect_type', redirectType)
          .maybeSingle()

        if (slugError && slugError.code !== 'PGRST116') {
          throw slugError
        }

        if (existingRedirect && (!excludeId || existingRedirect.id !== excludeId)) {
          return { available: false, reason: 'This slug is already in use' }
        }

        if (redirectType === 'root') {
          const { data: agentPage, error: agentError } = await supabase
            .from('agent_pages')
            .select('id')
            .eq('slug', cleanedSlug)
            .maybeSingle()

          if (agentError && agentError.code !== 'PGRST116') {
            throw agentError
          }

          if (agentPage) {
            return { available: false, reason: 'This slug conflicts with an existing page' }
          }

          const { data: userProfile, error: userError } = await supabase
            .from('users_pf')
            .select('id')
            .eq('username', cleanedSlug)
            .maybeSingle()

          if (userError && userError.code !== 'PGRST116') {
            throw userError
          }

          if (userProfile) {
            return { available: false, reason: 'This slug conflicts with an existing page' }
          }
        }

        return { available: true }
      } catch (err) {
        console.error('Error checking slug availability:', err)
        return { available: false, reason: 'Unable to verify slug availability' }
      } finally {
        setCheckingSlug(false)
      }
    },
    [sanitizeSlug]
  )

  const createRedirect = useCallback(
    async (payload) => {
      if (!user || !isAdmin) {
        throw new Error('You do not have permission to create redirects.')
      }

      const now = new Date().toISOString()
      const sanitizedSlug = sanitizeSlug(payload.slug)

      const { data, error: insertError } = await supabase
        .from('url_redirects')
        .insert({
          slug: sanitizedSlug,
          destination_url: payload.destination_url,
          description: payload.description || null,
          is_active: payload.is_active ?? true,
          redirect_type: payload.redirect_type || 'short',
          created_by: user.id,
          click_count: 0,
          created_at: now,
          updated_at: now
        })
        .select()
        .single()

      if (insertError) {
        console.error('Error creating redirect:', insertError)
        throw insertError
      }

      const normalized = { ...data, redirect_type: data.redirect_type || 'short' }

      setRedirects((prev) => {
        const updated = [normalized, ...prev]
        refreshAnalytics(updated)
        return updated
      })

      return normalized
    },
    [isAdmin, refreshAnalytics, sanitizeSlug, user]
  )

  const updateRedirect = useCallback(
    async (id, payload) => {
      if (!user || !isAdmin) {
        throw new Error('You do not have permission to update redirects.')
      }

      const now = new Date().toISOString()
      const updates = {
        ...payload,
        updated_at: now
      }

      if (payload.slug) {
        updates.slug = sanitizeSlug(payload.slug)
      }

      const { data, error: updateError } = await supabase
        .from('url_redirects')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (updateError) {
        console.error('Error updating redirect:', updateError)
        throw updateError
      }

      const normalized = { ...data, redirect_type: data.redirect_type || 'short' }

      setRedirects((prev) => {
        const updated = prev.map((redirect) => (redirect.id === id ? normalized : redirect))
        refreshAnalytics(updated)
        return updated
      })

      return normalized
    },
    [isAdmin, refreshAnalytics, sanitizeSlug, user]
  )

  const deleteRedirect = useCallback(
    async (id) => {
      if (!user || !isAdmin) {
        throw new Error('You do not have permission to delete redirects.')
      }

      const { error: deleteError } = await supabase
        .from('url_redirects')
        .delete()
        .eq('id', id)

      if (deleteError) {
        console.error('Error deleting redirect:', deleteError)
        throw deleteError
      }

      setRedirects((prev) => {
        const updated = prev.filter((redirect) => redirect.id !== id)
        refreshAnalytics(updated)
        return updated
      })
    },
    [isAdmin, refreshAnalytics, user]
  )

  const bulkUpdateStatus = useCallback(
    async (ids = [], isActive = true) => {
      if (!ids.length) return
      if (!user || !isAdmin) {
        throw new Error('You do not have permission to update redirects.')
      }

      const now = new Date().toISOString()

      const { data, error: bulkError } = await supabase
        .from('url_redirects')
        .update({
          is_active: isActive,
          updated_at: now
        })
        .in('id', ids)
        .select()

      if (bulkError) {
        console.error('Error performing bulk status update:', bulkError)
        throw bulkError
      }

      setRedirects((prev) => {
        const updated = prev.map((redirect) => {
          if (ids.includes(redirect.id)) {
            const updatedRedirect = data.find((item) => item.id === redirect.id)
            return updatedRedirect || { ...redirect, is_active: isActive, updated_at: now }
          }
          return redirect
        })
        refreshAnalytics(updated)
        return updated
      })

      return data
    },
    [isAdmin, refreshAnalytics, user]
  )

  const trackRedirectClick = useCallback(async (slug, redirectType = 'short') => {
    if (!slug) return { redirect: null, error: new Error('Missing slug') }

    const { data: redirect, error: fetchError } = await supabase
      .from('url_redirects')
      .select('*')
      .eq('slug', slug)
      .eq('redirect_type', redirectType)
      .eq('is_active', true)
      .maybeSingle()

    if (fetchError) {
      if (fetchError.code !== 'PGRST116') {
        console.error('Error fetching redirect for tracking:', fetchError)
      }
      return { redirect: null, error: fetchError }
    }

    if (!redirect) {
      return { redirect: null, error: null }
    }

    const { data: updated, error: updateError } = await supabase
      .from('url_redirects')
      .update({
        click_count: (redirect.click_count || 0) + 1,
        updated_at: new Date().toISOString()
      })
      .eq('id', redirect.id)
      .select()
      .single()

    if (updateError) {
      console.error('Error updating click count:', updateError)
      return { redirect, error: updateError }
    }

    const normalized = { ...updated, redirect_type: updated.redirect_type || redirect.redirect_type || 'short' }

    setRedirects((prev) => {
      const updatedList = prev.map((item) => (item.id === normalized.id ? normalized : item))
      refreshAnalytics(updatedList)
      return updatedList
    })

    return { redirect: normalized, error: null }
  }, [refreshAnalytics])

  const value = {
    redirects,
    loading,
    error,
    analytics,
    checkingSlug,
    isAdmin,
    fetchRedirects,
    createRedirect,
    updateRedirect,
    deleteRedirect,
    bulkUpdateStatus,
    checkSlugAvailability,
    trackRedirectClick,
    sanitizeSlug
  }

  return (
    <RedirectsContext.Provider value={value}>
      {children}
    </RedirectsContext.Provider>
  )
}

export default RedirectsContext
